import { consume } from '@lit/context';
import { gsap } from 'gsap';
import { LitElement, css, html } from 'lit';
import { customElement, query } from 'lit/decorators.js';

import { gameApiContext } from '../../context';
import type { GameAPIType } from '../../GameAPI';
import { tokens } from '../../theme/tokens.css';
import type { BindName, BindSlot } from '../../../game/types/Input';
import { uiState as uiStateProxy, type KeybindItem } from '../../UIState';
import { bindValtio } from '../../ValtioController';

/**
 * Keybinds Panel - Keyboard binding configuration
 *
 * Modal panel for customizing keyboard controls.
 * Displays the keybinds in two balanced columns with inline capture UX.
 *
 * Features:
 * - Two evenly-split columns of keybind items
 * - Primary and secondary key slots per binding
 * - Inline "press key" capture mode; Escape cancels it
 * - Right-click a slot to clear it
 * - Reset to defaults button
 * - GSAP entrance/exit animations
 * - ESC or backdrop click to close (when not editing)
 *
 * @element keybinds-panel
 */
@customElement('keybinds-panel')
export class KeybindsPanel extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        /* Declared here rather than inherited from the body: the panel owns
           its type, and a tap to close it shouldn't start a text selection,
           callout or a grey flash across the whole screen. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .backdrop {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--color-bg-backdrop);
        z-index: 200;
        /* Drops the 300ms double-tap delay from tap-to-close. */
        touch-action: manipulation;
      }

      .panel {
        position: relative;
        box-sizing: border-box;
        /* Room for the longest name, Toggle Leaderboard, on one line */
        width: 740px;
        max-width: calc(100vw - 32px);
        /* Of the backdrop, as the help panel's: room for the whole list on a
           1366x768 screen. */
        max-height: 88%;
        background-color: var(--color-bg-modal);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        cursor: default;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      .header {
        display: flex;
        align-items: baseline;
        gap: 16px;
        padding: 16px 24px;
        border-bottom: 1px solid var(--color-border-soft);
        flex-shrink: 0;
      }

      .header .title {
        font-size: var(--font-size-lg);
        font-weight: bold;
        color: var(--color-text-white);
      }

      .header .hint {
        flex: 1;
        color: var(--color-text-muted);
        font-size: var(--font-size-sm);
      }

      /* Brightens while a slot waits for its key, so the change of wording
         catches the eye */
      .header .hint.waiting {
        color: var(--color-text-white);
      }

      .reset-btn {
        padding: 8px 16px;
        background-color: var(--color-bg-badge);
        border: none;
        border-radius: var(--radius-sm);
        color: var(--color-text-white);
        font-family: var(--font-family);
        font-size: var(--font-size-sm);
        cursor: pointer;
        transition: background-color 0.15s;
      }

      @media (hover: hover) {
        .reset-btn:hover {
          background-color: var(--color-bg-item-hover);
        }
      }

      .keybinds-list {
        display: flex;
        padding: 12px 24px 16px 24px;
        gap: 40px;
        overflow-y: auto;
        flex: 1;
      }

      .left-binds,
      .right-binds {
        flex: 1;
      }

      /* Legacy's row spacing, scaled to this panel's smaller text (its rows
         were 30px for 14px keys): with this many binds, rows any taller
         push the bottom of the list off a laptop screen */
      .item {
        display: flex;
        align-items: center;
        padding: 3px 0;
      }

      .item .name {
        flex: 1;
        color: var(--color-text-primary);
        font-size: var(--font-size-sm);
      }

      .item .bind {
        width: 70px;
        padding: 3px 8px;
        margin-left: 8px;
        background-color: var(--color-bg-input);
        border-radius: var(--radius-sm);
        text-align: center;
        color: var(--color-text-bright);
        font-size: var(--font-size-sm);
        cursor: pointer;
        transition: background-color 0.15s;
      }

      @media (hover: hover) {
        .item .bind:hover {
          background-color: var(--color-bg-item-hover);
        }
      }

      .item .bind.blank {
        color: var(--color-text-muted);
      }

      .item .bind.editing {
        background-color: var(--color-btn-login);
        color: var(--color-text-white);
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** GameAPI instance for keybinds and panel control */
  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  /** Reference to the panel element for GSAP animations */
  @query('.panel') private panel?: HTMLElement;

  /** Reference to the backdrop element for GSAP animations */
  @query('.backdrop') private backdrop?: HTMLElement;

  /** Guard against re-entrant close calls */
  private isClosing = false;

  /** @override Registers keyboard listener */
  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleKeyDown, { capture: true });
  }

  /** @override Cleans up keyboard listener and GSAP animations on disconnect */
  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeyDown, { capture: true });

    // Clean up GSAP animations
    if (this.panel) gsap.killTweensOf(this.panel);
    if (this.backdrop) gsap.killTweensOf(this.backdrop);
  }

  /** @override Triggers entrance animation. Guard, don't reset: a close that
   *  lands before first render must not be undone into a ghost entrance. */
  override firstUpdated() {
    if (this.isClosing) return;
    this.animateIn();
  }

  /** Animates the panel and backdrop into view */
  private animateIn() {
    if (!this.panel || !this.backdrop) return;

    // Kill any existing tweens to prevent overlap
    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panel);

    gsap.fromTo(this.backdrop, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' });
    gsap.fromTo(this.panel, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' });
  }

  /**
   * Animates the panel and backdrop out of view
   * @param onComplete - Callback invoked after animation completes
   */
  private animateOut(onComplete: () => void) {
    if (!this.panel || !this.backdrop) {
      onComplete();
      return;
    }

    // Kill any existing tweens to prevent overlap/double callbacks
    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panel);

    gsap.to(this.backdrop, { opacity: 0, duration: 0.3, ease: 'power2.in' });
    gsap.to(this.panel, { opacity: 0, scale: 0.9, duration: 0.3, ease: 'power2.in', onComplete });
  }

  /** Handles Escape to close the panel. Consumed (capture phase) when NOT
   *  editing, so the modal owns its dismissal and animates out — otherwise the
   *  game-side Escape path (UI.ts controlKey → closeAllPanels) would set
   *  activePanel synchronously and cut the exit animation short. During an edit
   *  it is deliberately let through: Input.ts uses Escape to cancel the edit. */
  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (this.api.isEditingKeybind()) return;
    e.preventDefault();
    e.stopPropagation();
    // An Escape held to cancel an edit mustn't go on to close the panel once
    // the key starts repeating
    if (e.repeat) return;
    this.close();
  };

  /** Handles backdrop click - cancels edit if active, otherwise closes panel */
  private handleBackdropClick = (e: Event) => {
    e.stopPropagation();
    if (this.api.isEditingKeybind()) {
      // Misclick while editing: cancel edit but keep panel open
      this.api.cancelBindEdit();
      return;
    }
    this.close();
  };

  /** Animates out and closes the panel via GameAPI */
  private close() {
    if (this.isClosing) return;
    this.isClosing = true;
    this.animateOut(() => this.api.closePanel());
  }

  /**
   * Handles click on a keybind slot to start editing.
   * Explicitly cancels any active edit before starting a new one.
   * @param bindName - The bind action name (e.g., "UP", "FIRE")
   * @param bindIndex - The slot index (0 = primary, 1 = secondary)
   */
  private handleBindClick(bindName: BindName, bindIndex: BindSlot) {
    this.api.playClick();
    if (this.api.isEditingKeybind()) {
      this.api.cancelBindEdit();
    }
    this.api.startBindEdit(bindName, bindIndex);
  }

  /**
   * Handles right-click on a keybind slot to clear it (ending any edit).
   * @param bindName - The bind action name (e.g., "UP", "FIRE")
   * @param bindIndex - The slot index (0 = primary, 1 = secondary)
   */
  private handleBindContextMenu(e: MouseEvent, bindName: BindName, bindIndex: BindSlot) {
    e.preventDefault();
    this.api.playClick();
    this.api.clearBind(bindName, bindIndex);
  }

  /** Handles reset button click to restore default keybinds */
  private handleReset = () => {
    this.api.playClick();
    if (this.api.isEditingKeybind()) this.api.cancelBindEdit();
    this.api.resetKeybinds();
  };

  /**
   * Renders a single keybind item row.
   * @param item - The keybind item data
   */
  private renderKeybindItem(item: KeybindItem) {
    const { editingBindName, editingBindIndex } = this.uiState.keybinds;
    const isEditingPrimary = editingBindName === item.bindName && editingBindIndex === 0;
    const isEditingSecondary = editingBindName === item.bindName && editingBindIndex === 1;

    return html`
      <div class="item">
        <div class="name">${item.displayName}</div>
        <div
          class="bind ${!item.primaryKey ? 'blank' : ''} ${isEditingPrimary ? 'editing' : ''}"
          @click=${() => this.handleBindClick(item.bindName, 0)}
          @contextmenu=${(e: MouseEvent) => this.handleBindContextMenu(e, item.bindName, 0)}
        >
          ${isEditingPrimary ? 'press key' : (item.primaryKey ?? '—')}
        </div>
        <div
          class="bind ${!item.secondaryKey ? 'blank' : ''} ${isEditingSecondary ? 'editing' : ''}"
          @click=${() => this.handleBindClick(item.bindName, 1)}
          @contextmenu=${(e: MouseEvent) => this.handleBindContextMenu(e, item.bindName, 1)}
        >
          ${isEditingSecondary ? 'press key' : (item.secondaryKey ?? '—')}
        </div>
      </div>
    `;
  }

  /** @override Renders the modal panel with keybind columns */
  override render() {
    const { leftColumn, rightColumn, editingBindName } = this.uiState.keybinds;
    const waiting = editingBindName != null;

    return html`
      <div class="backdrop" @click=${this.handleBackdropClick}>
        <div class="panel" @click=${(e: Event) => e.stopPropagation()}>
          <div class="header">
            <span class="title">Keybinds</span>
            <span class="hint ${waiting ? 'waiting' : ''}">
              ${waiting ? 'Press the new key, or Esc to cancel' : 'Click a key to change it, right-click to clear it'}
            </span>
            <button class="reset-btn" @click=${this.handleReset}>Reset to Defaults</button>
          </div>
          <div class="keybinds-list">
            <div class="left-binds">${leftColumn.map((item) => this.renderKeybindItem(item))}</div>
            <div class="right-binds">${rightColumn.map((item) => this.renderKeybindItem(item))}</div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'keybinds-panel': KeybindsPanel;
  }
}
